import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { ArrowRight, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useProfile } from '@/api/profile'
import { useLiveSnapshot } from '@/api/liveSnapshot'
import { useManagerNavigation } from './useManagerNavigation'
import { LiveKpiCards } from './live/LiveKpiCards'
import { LiveCallsList } from './live/LiveCallsList'
import { AgentStatusList } from './live/AgentStatusList'

/**
 * Vue Live — radar permanent du manager.
 *
 * Pas de polling : on charge le snapshot une fois, puis on invalide via les
 * events SSE `call` (voir useLiveSnapshot). Le manager peut laisser cette
 * vue ouverte toute la journée sans charger le backend inutilement.
 */
export function LiveView() {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const darkMode = useUIStore((s) => s.darkMode)
  const { data: profile } = useProfile()
  const { data, isLoading, isFetching, error, refetch } = useLiveSnapshot(profile?.company)
  // Seul geste offert ici : passer à l'Historique. Le Live est une vue
  // d'observation — le manager n'est pas participant des appels affichés,
  // aucun contrôle d'appel (raccrocher, transférer…) n'a sa place.
  const { setActiveTab } = useManagerNavigation()

  const pageBg = darkMode ? 'bg-slate-900' : 'bg-slate-50'
  const titleColor = darkMode ? 'text-white' : 'text-slate-900'
  const subtitleColor = darkMode ? 'text-slate-400' : 'text-slate-500'

  return (
    <div className={cn('h-full flex flex-col', pageBg)}>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-6 space-y-5">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1
                className={cn(
                  'text-lg font-bold tracking-tight flex items-center gap-2.5',
                  titleColor,
                )}
              >
                <span className="relative inline-flex h-2 w-2 flex-shrink-0">
                  {data && (
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  )}
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                {t('manager.live.sectionTitle')}
              </h1>
              <p className={cn('text-[13px] mt-0.5', subtitleColor)}>
                {t('manager.live.sectionSubtitle')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void refetch()}
                disabled={isFetching}
                className={cn(
                  'inline-flex items-center justify-center h-8 w-8 rounded-lg border',
                  'focus:outline-none focus:ring-2 focus:ring-indigo-500/40 disabled:opacity-50',
                  darkMode
                    ? 'border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                )}
                title={t('manager.live.refresh')}
              >
                <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('history')}
                className={cn(
                  'inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border text-[13px] font-medium',
                  'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                  darkMode
                    ? 'border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                )}
              >
                {t('manager.live.openHistory')}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="space-y-4">
              {/* Une barre KPI, deux panneaux — mêmes gabarits que le rendu réel. */}
              <div
                className={cn(
                  'h-[76px] rounded-[10px] animate-pulse',
                  darkMode ? 'bg-slate-800' : 'bg-slate-200/70',
                )}
              />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div
                  className={cn(
                    'h-96 rounded-[10px] animate-pulse lg:col-span-2',
                    darkMode ? 'bg-slate-800' : 'bg-slate-200/70',
                  )}
                />
                <div
                  className={cn(
                    'h-96 rounded-[10px] animate-pulse',
                    darkMode ? 'bg-slate-800' : 'bg-slate-200/70',
                  )}
                />
              </div>
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div
              className={cn(
                'rounded-xl border p-6 text-center',
                darkMode ? 'border-rose-500/30 bg-rose-500/10' : 'border-rose-200 bg-rose-50',
              )}
            >
              <p className="text-rose-600 dark:text-rose-400 font-medium">
                {error instanceof Error ? error.message : t('manager.live.loadError')}
              </p>
            </div>
          )}

          {/* Data */}
          {data && !isLoading && (
            <>
              <LiveKpiCards kpis={data.kpis} darkMode={darkMode} />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <LiveCallsList
                    inProgress={data.calls_in_progress}
                    recent={data.recent_calls}
                    serverTime={data.server_time}
                    locale={locale}
                    darkMode={darkMode}
                  />
                </div>
                <div className="lg:col-span-1">
                  <AgentStatusList agents={data.agents} darkMode={darkMode} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
